import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { ContentCopy } from '@mui/icons-material'

import { getAllUsersInSession } from '../../../service/userService'
import { getOffloadingSummary } from '../../../service/experimentService'
import { SessionDataContext } from '../../instructorDashboard/context/SessionDataContext'
import { TelemetryDataContext, TelemetryDataProvider } from '../../instructorDashboard/context/TelemetryDataContext'
import {
  card,
  cardHeader,
  cardSubtitle,
  cardTitle,
  dashboardPage,
  toolbarButton,
} from '../../instructorDashboard/components/dashboardStyles'
import { showToast } from '../../commonUnit/Toast'
import { studentPathFor } from '../studentView/studentSurface'
import { aiChatEnabled } from '../sessionSettings'
import { assignmentLabel, offloadingLabel, publishedOffloading } from './experimentConfig'
import { usesOffloading } from './experimentExport'
import ExperimentExportButtons from './ExperimentExportButtons'

const ROSTER_POLL_MS = 20000

// The Monitor's telemetry statistics for one condition (TelemetryDataContext.stats), polled
// live, handed up to the page once loaded. Renders nothing. SessionDataContext only needs the
// session id here - the full SessionDataProvider would add a session poll the page already does.
const StatsReporter = ({ sessionId, onStats }) => {
  const { stats, loading } = useContext(TelemetryDataContext)
  // The provider starts out not loading, with empty stats, before its first fetch: report only
  // after a load has begun, so a card shows its spinner instead of "0 of 0".
  const sawLoadRef = useRef(false)
  useEffect(() => {
    if (loading) sawLoadRef.current = true
    else if (sawLoadRef.current) onStats(sessionId, stats)
  }, [sessionId, stats, loading]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

const ConditionStatsSource = ({ sessionId, onStats }) => {
  const value = useMemo(() => ({ sessionId }), [sessionId])
  return (
    <SessionDataContext.Provider value={value}>
      <TelemetryDataProvider live>
        <StatsReporter sessionId={sessionId} onStats={onStats} />
      </TelemetryDataProvider>
    </SessionDataContext.Provider>
  )
}

// Same name rule as the Monitor's student list (ClassOverview).
const displayName = (student) =>
  student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'

const percent = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—')

const formatMinutes = (seconds) => (seconds ? `${Math.max(1, Math.round(seconds / 60))} min` : '—')

const formatLastActive = (iso) => {
  if (!iso) return '—'
  const minutesAgo = Math.round((Date.now() - Date.parse(iso)) / 60000)
  if (minutesAgo < 1) return 'just now'
  if (minutesAgo < 60) return `${minutesAgo} min ago`
  return new Date(iso).toLocaleString()
}

const statLabel = { fontSize: '13px', color: '#9ca3af' }
const statValue = { fontSize: '28px', fontWeight: 700, color: '#111', lineHeight: 1.2 }
const statNote = { fontSize: '13px', fontWeight: 400, color: '#9ca3af', marginLeft: '6px' }
const cellSx = { borderBottom: '1px solid #f3f4f6', fontSize: '14px' }
const headSx = { ...cellSx, color: '#6b7280', fontWeight: 600 }
// The app's global table rules (codeIssuePanel.scss: thead, th, td) draw heavy outer and
// column borders; the Monitor's lists use light row lines only.
const tableSx = {
  border: 'none',
  '& thead': { border: 'none', textAlign: 'left' },
  '& tr': { border: 'none' },
  '& th, & td': { borderLeft: 'none', borderRight: 'none', borderTop: 'none' },
}

// A concept label with a count, as on the student's own strip.
const conceptChip = {
  fontSize: '12px', color: '#374151', background: '#f9fafb',
  border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1px 8px',
}

const Stat = ({ label, children }) => (
  <div>
    <div style={statLabel}>{label}</div>
    <div style={statValue}>{children}</div>
  </div>
)

// The experiment page's Compare conditions tab (its Overview until the Overview became the
// class as a whole): a card per condition, every student in one table with their condition,
// and one export for all conditions - in the Monitor's card style. `conditions` are the
// config's conditions, each with `row` (its session row, polled by the page) when it still
// exists.
const ExperimentCompareTab = ({ experimentId, config, conditions, onOpenCondition, onOpenStudent }) => {
  const [statsBySession, setStatsBySession] = useState({})
  const [rosterBySession, setRosterBySession] = useState({})
  // What students asked the AI about (offloadingService.experimentSummary), when any condition
  // runs the offloading analysis; null until loaded.
  const [offloading, setOffloading] = useState(null)

  const live = conditions.filter((c) => c.row)
  const liveIds = live.map((c) => c.sessionId).join(',')
  const showOffloading = usesOffloading(conditions)

  useEffect(() => {
    if (!liveIds) return
    let cancelled = false
    const load = async () => {
      const ids = liveIds.split(',').map(Number)
      const lists = await Promise.all(ids.map((id) => getAllUsersInSession(id)))
      if (cancelled) return
      setRosterBySession(
        Object.fromEntries(ids.map((id, i) => [id, (lists[i] || []).filter((u) => u.role === 3 || u.role === 4)]))
      )
      if (!showOffloading) return
      try {
        const summary = await getOffloadingSummary(experimentId)
        if (!cancelled) setOffloading(summary)
      } catch (error) {
        // Keep the last summary; the next round tries again.
      }
    }
    load()
    const timer = setInterval(load, ROSTER_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [liveIds, experimentId, showOffloading])

  const conceptLabel = (id) => offloading?.concepts?.find((concept) => concept.id === id)?.label || id
  const offloadingOf = (condition) => offloading?.conditions?.find((c) => c.key === condition.key) || null
  // Per concept, how many of the condition's students asked the AI about it, most first.
  const conceptCounts = (condition) => {
    const counts = new Map()
    Object.values(offloadingOf(condition)?.students || {}).forEach((student) =>
      student.offloaded.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1))
    )
    return [...counts].sort((a, b) => b[1] - a[1])
  }

  const handleStats = (sessionId, stats) => setStatsBySession((prev) => ({ ...prev, [sessionId]: stats }))

  const students = useMemo(
    () =>
      live.flatMap((condition) => {
        const summaries = statsBySession[condition.sessionId]?.studentSummaries || {}
        return (rosterBySession[condition.sessionId] || [])
          .map((student) => ({ condition, student, summary: summaries[String(student.id)] || null }))
          .sort((a, b) => displayName(a.student).localeCompare(displayName(b.student)))
      }),
    [live, statsBySession, rosterBySession] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const copyLink = (condition) => {
    navigator.clipboard.writeText(`${window.location.origin}${studentPathFor(condition.row)}`)
    showToast(`${condition.name}: student link copied to clipboard!`, 'success', 2000)
  }

  return (
    <div style={dashboardPage}>
      {live.map((condition) => (
        <ConditionStatsSource key={condition.sessionId} sessionId={condition.sessionId} onStats={handleStats} />
      ))}

      <div style={card}>
        <div style={cardHeader}>
          <h3 style={cardTitle}>{config.name}</h3>
        </div>
        <p style={{ ...cardSubtitle, margin: 0 }}>
          {config.problemName && <>Exercise: {config.problemName} · </>}
          {conditions.length} conditions · Assigning students: {assignmentLabel(config.assignment)}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(conditions.length, 3)}, 1fr)`, gap: '20px' }}>
        {conditions.map((condition) => {
          const row = condition.row
          const stats = statsBySession[condition.sessionId]
          const summaries = Object.values(stats?.studentSummaries || {}).filter((s) => s.testsTotal > 0)
          const meanTests = summaries.length
            ? summaries.reduce((sum, s) => sum + s.testsPassed / s.testsTotal, 0) / summaries.length
            : null
          const chatOn = row ? aiChatEnabled(row) : condition.aiChat
          const loading = <CircularProgress size={20} />
          return (
            <div key={condition.key} style={{ ...card, display: 'flex', flexDirection: 'column' }}>
              <div style={cardHeader}>
                <h3 style={cardTitle}>{condition.name}</h3>
              </div>
              <p style={cardSubtitle}>
                {chatOn ? 'AI chat' : 'No AI chat'}
                {chatOn && condition.offloading !== 'off' ? ` · ${offloadingLabel(condition.offloading)}` : ''}
              </p>
              {!row ? (
                <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                  This condition&apos;s session was deleted from the session list.
                </p>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px', marginBottom: '20px' }}>
                    <Stat label="Joined">{row.stu_num || 0}</Stat>
                    <Stat label="Connected now">{row.active_stu || 0}</Stat>
                    <Stat label="Passed all tests">
                      {stats ? (
                        <>
                          {stats.finishedStudents} of {stats.totalStudents}
                          <span style={statNote}>{percent(stats.finishedStudents, stats.totalStudents)}</span>
                        </>
                      ) : (
                        loading
                      )}
                    </Stat>
                    <Stat label="Tests passed (avg)">
                      {stats ? (meanTests == null ? '—' : `${Math.round(meanTests * 100)}%`) : loading}
                    </Stat>
                    <Stat label="AI questions">
                      {stats ? (
                        <>
                          {stats.latestConversations?.totalConversations || 0}
                          {stats.totalStudents > 0 && (
                            <span style={statNote}>
                              {(stats.latestConversations.totalConversations / stats.totalStudents).toFixed(1)} per student
                            </span>
                          )}
                        </>
                      ) : (
                        loading
                      )}
                    </Stat>
                  </div>
                  {publishedOffloading(condition) !== 'off' && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ ...statLabel, marginBottom: '6px' }}>Asked the AI about (students)</div>
                      {!offloading ? (
                        <CircularProgress size={16} />
                      ) : conceptCounts(condition).length === 0 ? (
                        <span style={{ fontSize: '14px', color: '#9ca3af' }}>No concept yet</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {conceptCounts(condition).map(([id, count]) => (
                            <span key={id} style={conceptChip}>
                              {conceptLabel(id)} · {count}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button type="button" style={toolbarButton} onClick={() => copyLink(condition)}>
                      <ContentCopy sx={{ fontSize: 14 }} />
                      Copy link
                    </button>
                    <button type="button" style={toolbarButton} onClick={() => onOpenCondition(condition.key)}>
                      Open monitor
                    </button>
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <div style={cardHeader}>
              <h3 style={cardTitle}>Students</h3>
            </div>
            <p style={cardSubtitle}>
              Every condition&apos;s students. Click a student with activity to open their detail in their
              condition&apos;s Monitor. Updated about every 20 seconds.
            </p>
          </div>
          <ExperimentExportButtons experimentId={experimentId} experimentName={config.name} conditions={conditions} />
        </div>
        {students.length === 0 ? (
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '14px' }}>No students have joined any condition yet.</p>
        ) : (
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Condition</TableCell>
                <TableCell sx={headSx}>Student</TableCell>
                <TableCell sx={headSx} align="right">Tests passed</TableCell>
                <TableCell sx={headSx} align="right">AI questions</TableCell>
                {showOffloading && <TableCell sx={headSx}>Asked the AI about</TableCell>}
                <TableCell sx={headSx} align="right">Time on task</TableCell>
                <TableCell sx={headSx} align="right">Last active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map(({ condition, student, summary }) => (
                <TableRow
                  key={`${condition.key}-${student.id}`}
                  hover={Boolean(summary)}
                  sx={{ cursor: summary ? 'pointer' : 'default' }}
                  onClick={summary ? () => onOpenStudent(condition.key, student.id) : undefined}
                  title={summary ? 'Open this student in their condition’s Monitor' : 'No activity yet'}>
                  <TableCell sx={cellSx}>{condition.name}</TableCell>
                  <TableCell sx={cellSx}>{displayName(student)}</TableCell>
                  <TableCell sx={cellSx} align="right">
                    {summary?.testsTotal ? `${summary.testsPassed} / ${summary.testsTotal}` : '—'}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">{summary ? summary.chatCount : '—'}</TableCell>
                  {showOffloading && (
                    <TableCell sx={cellSx}>
                      {(offloadingOf(condition)?.students?.[String(student.id)]?.offloaded || []).map(conceptLabel).join(', ') ||
                        '—'}
                    </TableCell>
                  )}
                  <TableCell sx={cellSx} align="right">{formatMinutes(summary?.duration)}</TableCell>
                  <TableCell sx={cellSx} align="right">{formatLastActive(summary?.lastActive)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}

export default ExperimentCompareTab
